/* eslint-disable react/prop-types */
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import Checkbox from '@material-ui/core/Checkbox'
import MuiTextField from '@material-ui/core/TextField'
import { XDivider, YDivider, FlexByRowCentered } from 'irmlib/styles'
import { Label, ThemeButton, OutlinedButton, ContainedButton } from 'irmlib/components/Controls'
import { CancelButton, TextField } from 'irmlib/components/FormControls'
import * as gdeTransactions from '../../../services/firestore/gdeTransactions'
import { LocalToast, setBusy, clearBusy, openGeneralDialog, closeGeneralDialog } from 'irmlib/containers'
import PropTypes from 'prop-types'
import EditIcon from '@material-ui/icons/Edit'
import * as constants from 'irmlib/common/constants'
import {
  userTypes,
  QC_GRID_DEFAULT_PAGE_SIZE,
  QC_GRID_PAGE_SIZE_OPTIONS,
  QC_GRID_ROW_BUFFER,
  QC_GRID_MIN_VISIBLE_ROWS,
  QC_GRID_VIEW_ROW_HEIGHT,
  QC_GRID_EDIT_ROW_BASE_HEIGHT,
  QC_GRID_ERROR_LINE_HEIGHT,
  QC_GRID_ERROR_CHARS_PER_LINE,
  QC_GRID_ERROR_ROW_PADDING,
  QC_GRID_HEADER_HEIGHT,
  QC_GRID_TOOLBAR_HEIGHT,
  QC_GRID_FOOTER_HEIGHT,
  QC_GRID_DEFAULT_HEIGHT,
} from '../../../common/constants'
import { successToast, errorToast } from '../../../common/functions'
import * as routePaths from '../../../navigation/routePaths'
import { useTranslation } from 'react-i18next'
import { ClearOutlined, DoneOutlined } from '@material-ui/icons'
import { DataGrid, GridActionsCellItem, GridToolbarColumnsButton, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid'

const DATE_PAIR_FIELDS = {
  fromDate: 'toDate',
  toDate: 'fromDate',
  fromDate_verify: 'toDate_verify',
  toDate_verify: 'fromDate_verify',
}

const SKPBOX = 0
const DIV = 1
const DEPT = 2

const BoxFlex = styled('div')`
  display: flex;
  float: right;
  margin: -16px;
`

// AI code start — file QC action toolbar layout
const QcFileActionToolbar = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin: 8px 0 12px;
  gap: 10px;
`
// AI code end
const BoxSet = styled('div')`
  display: flex;
`
const BoxSetContainer = styled('div')`
  width: 100%;
`

const BoxTextfield = styled('div')`
  width: 100%;
`

const BottomPaddedDiv = styled('div')`
  padding-bottom: 20px;
`

const TableContainer = styled(BottomPaddedDiv)`
  width: 100%;
`

const VerticalDivider = () => <YDivider height="30px" />

const BottomPaddedButton = (props) => (
  <>
    <ThemeButton {...props} />
    <VerticalDivider />
  </>
)

const CountStyle = styled('div')`
  margin-top: -36px;
  font-size: 16px;
  height: 0px;
  color: #1b75bc;
`

const DivWrap = styled('div')`
  display: flex;
  justify-content: center;
  width: 100%;
`

const DivCenter = styled('div')`
  display: flex;
  justify-content: center;
  white-space: nowrap;
  width: 100%;
  margin-bottom: -8px;
`

const GridContainer = styled('div')`
  width: 100%;
`

const DivWarning = styled('div')`
  display: flex;
  justify-content: center;
  white-space: nowrap;
  width: 100%;
`

const GRID_ROW_ID = '__bulkUpdateRowId'

const getGridRowId = (row, index) => row?.[GRID_ROW_ID] || `${row?.skpBoxId ?? ''}-${row?.fileId ?? ''}-${index}`

const assignGridRowIds = (list) => (list || []).map((row, index) => ({
  ...row,
  [GRID_ROW_ID]: getGridRowId(row, index),
}))

const stripGridRowProps = (row) => {
  const { [GRID_ROW_ID]: _gridRowId, tableData: _tableData, ...data } = row || {}
  return data
}

const stripGridInternalProps = (row) => {
  const { [GRID_ROW_ID]: _gridRowId, ...data } = row || {}
  return data
}

const QcGridToolbar = ({ setColumnsButtonEl }) => (
  <GridToolbarContainer sx={{ marginLeft: 'auto' }}>
    <GridToolbarColumnsButton
      ref={setColumnsButtonEl}
      slotProps={{
        button: {
          children: '',
          'aria-label': 'Columns',
        },
      }}
    />
  </GridToolbarContainer>
)

const getEditErrorKey = (rowId, field) => `${rowId}-${field}`

const getValidationMessage = (validation) => {
  if (validation === true || validation === undefined) return ''
  if (typeof validation === 'string') return validation
  return validation.helperText || validation.message || ''
}

// AI code start — QC grid validation display and dynamic row height helpers
const buildDuplicateValueCounts = (fileList, columnsList) => {
  const counts = new Map()
  const duplicateFields = columnsList
    .filter((column) => column.doNotDuplicate && column.field)
    .map((column) => column.field)
  if (!duplicateFields.length || !fileList?.length) {
    return counts
  }
  fileList.forEach((row) => {
    duplicateFields.forEach((field) => {
      const value = row[field]
      if (value === undefined || value === null || value === '') {
        return
      }
      const key = `${field}\u0000${String(value)}`
      counts.set(key, (counts.get(key) || 0) + 1)
    })
  })
  return counts
}

const formatCellValidationMessage = (message, t) => {
  if (!message) return ''
  if (typeof message === 'string' && message.includes('.') && !message.includes(' ')) {
    return t(message)
  }
  return message
}

const estimateMessageLineCount = (message, charsPerLine = QC_GRID_ERROR_CHARS_PER_LINE) => {
  if (!message) return 0
  const text = String(message)
  const explicitLines = text.split('\n').length
  const wrappedLines = Math.ceil(text.length / charsPerLine)
  return Math.max(1, explicitLines, wrappedLines)
}

const buildErrorLinesByRowIndex = (formattedCellErrors) => {
  const errorLinesByRowIndex = new Map()
  Object.entries(formattedCellErrors || {}).forEach(([key, message]) => {
    if (!message) return
    const match = key.match(/^(\d+)(.+)$/)
    if (!match) return
    const rowIndex = Number(match[1])
    const lines = estimateMessageLineCount(message)
    errorLinesByRowIndex.set(rowIndex, Math.max(errorLinesByRowIndex.get(rowIndex) || 0, lines))
  })
  return errorLinesByRowIndex
}

const getEditErrorMaxLines = (rowId, editErrors, formatMessage) => {
  let maxLines = 0
  Object.entries(editErrors || {}).forEach(([key, message]) => {
    if (!key.startsWith(`${rowId}-`) || !message) return
    maxLines = Math.max(maxLines, estimateMessageLineCount(formatMessage(message)))
  })
  return maxLines
}

const estimateRowHeightFromLines = (baseHeight, maxLines) => {
  if (!maxLines) {
    return baseHeight
  }
  return baseHeight + QC_GRID_ERROR_ROW_PADDING + (Math.max(maxLines, 2) * QC_GRID_ERROR_LINE_HEIGHT)
}

const rowNeedsDynamicHeight = (row, editingRowId, errorLinesByRowIndex) => {
  if (!row) {
    return false
  }
  if (row[GRID_ROW_ID] === editingRowId) {
    return true
  }
  const rowIndex = row.tableData?.id
  return errorLinesByRowIndex.has(rowIndex)
}

const computeQcGridContainerHeight = (resolveRowHeight, rowSample) => {
  const chrome = QC_GRID_HEADER_HEIGHT + QC_GRID_TOOLBAR_HEIGHT + QC_GRID_FOOTER_HEIGHT
  const sampleRows = rowSample.slice(0, QC_GRID_MIN_VISIBLE_ROWS)
  const rowHeights = sampleRows.map(resolveRowHeight)
  const viewportBodyHeight = Math.max(
    rowHeights.reduce((sum, height) => sum + height, 0),
    QC_GRID_MIN_VISIBLE_ROWS * QC_GRID_VIEW_ROW_HEIGHT,
  )
  return chrome + viewportBodyHeight
}

// AI code end

// AI code start — QC view/edit cell rendering
const QcViewCell = React.memo(({ helperText, value }) => (
  <div style={{ width: '100%', lineHeight: 1.35, overflow: 'visible' }}>
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    {helperText
      ? (
        <div
          style={{
            color: '#f44336',
            fontSize: '0.75rem',
            lineHeight: 1.35,
            marginTop: 4,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflow: 'visible',
          }}
          >
          {helperText}
        </div>
        )
      : null}
  </div>
))

const QcManualEditTextField = ({ autoFocus, column, field, helperText: initialHelperText, onDraftChange, rowId, value: initialValue }) => {
  const [value, setValue] = useState(initialValue ?? '')
  const [helperText, setHelperText] = useState(initialHelperText || '')

  useEffect(() => {
    setValue(initialValue ?? '')
  }, [initialValue])

  useEffect(() => {
    setHelperText(initialHelperText || '')
  }, [initialHelperText])

  const handleChange = (event) => {
    const nextValue = event.target.value
    setValue(nextValue)
    const validationMessage = onDraftChange(rowId, column, field, nextValue)
    setHelperText(validationMessage)
  }

  const stopGridKeyboardHandling = (event) => {
    event.stopPropagation()
  }

  return (
    <MuiTextField
      autoFocus={autoFocus}
      error={Boolean(helperText)}
      fullWidth
      helperText={helperText}
      FormHelperTextProps={{
        component: 'div',
        style: {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.35,
          marginRight: 0,
        },
      }}
      onChange={handleChange}
      onClick={event => event.stopPropagation()}
      onKeyDown={stopGridKeyboardHandling}
      onKeyUp={stopGridKeyboardHandling}
      value={value}
      variant="standard"
    />
  )
}
// AI code end

const QcDataGridBulkUpdateTable = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const {
    columnsList,
    isViewAllFiles,
    currentTemplateType,
    qualityCheck,
    currentUserType,
    dataEntryType,
    jobId,
    editTable,
    isFromQC,
    jobStatus,
    filesLoading,
    localEditOnly = false,
    onSelectionChange: onSelectionChangeProp,
    cellValidationErrors,
    onRowsUpdated,
  } = props
  const useCompactQcLayout = qualityCheck || isFromQC
  let { indexPointerOfFile } = props
  const [enable, setEnable] = useState(true)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [filesLength, setFilesLength] = useState()
  const [fileList, setFileList] = useState([])
  const location = useLocation()
  const pathParams = location.pathname.split('/')
  const isBoxList = pathParams[1]
  const [boxListWithStatus, setBoxListWithStatus] = useState([])
  const [editingRowId, setEditingRowId] = useState(null)
  const [editDraft, setEditDraft] = useState(null)
  const editDraftRef = useRef(null)
  const [editErrors, setEditErrors] = useState({})
  const apiRef = useGridApiRef()
  const gridScrollSnapshotRef = useRef({ top: 0, left: 0 })
  const shouldRestoreGridScrollRef = useRef(false)
  const validationSyncKeyRef = useRef('')
  const [columnsButtonEl, setColumnsButtonEl] = useState(null)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: QC_GRID_DEFAULT_PAGE_SIZE,
  })
  const handleDraftFieldChangeRef = useRef(() => {})

  const captureGridScroll = () => {
    const scrollPosition = apiRef.current?.getScrollPosition?.()
    if (scrollPosition) {
      gridScrollSnapshotRef.current = scrollPosition
      shouldRestoreGridScrollRef.current = true
    }
  }

  const restoreGridScroll = () => {
    if (!shouldRestoreGridScrollRef.current) return
    shouldRestoreGridScrollRef.current = false
    const { top, left } = gridScrollSnapshotRef.current
    requestAnimationFrame(() => {
      apiRef.current?.scroll?.({ top, left })
    })
  }

  useEffect(() => {
    setFileList(assignGridRowIds(props.fileList))
    setBoxListWithStatus(props.boxListWithStatus)
    setEditingRowId(null)
    setEditErrors({})
    editDraftRef.current = null
    setEditDraft(null)
    validationSyncKeyRef.current = ''
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }, [props.fileList])

  useEffect(() => {
    restoreGridScroll()
  }, [editingRowId, fileList])

  useEffect(() => {
    if (props.tableRef) {
      props.tableRef.current = {
        dataManager: {
          data: fileList
        },
        getRows: () => fileList
      }
    }
  }, [fileList, props.tableRef])

  const refLocalToast = useRef(null)

  const modalFormTemplate = [
    {
      label: t('dataEntry.skpBoxID'),
      fieldKey: 'obj.obj1.skpId',
    },
    {
      label: t('dataEntry.divisionID'),
      fieldKey: 'obj.obj1.divisionId',
    },
    {
      label: t('dataEntry.departmentID'),
      fieldKey: 'obj.obj1.departmentId',
    }
  ]

  const onSelectionChange = (rows) => {
    if (rows?.length > 0 && !isViewAllFiles) {
      setEnable(false)
    } else {
      setEnable(true)
    }
    setSelectedFiles(rows)
    setFilesLength(rows.length)
    onSelectionChangeProp?.(rows)
  }

  const compareData = (obj1, obj2, keyToIgnore) => {
    if (obj1 === obj2) return true
  
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) return false
    const keys1 = Object.keys(obj1)
  
    for (const key of keys1) {
      if (key === keyToIgnore) continue
      if (!Object.prototype.hasOwnProperty.call(obj2, key) || !compareData(obj1[key], obj2[key], keyToIgnore)) return false
    }
    return true
  }

  const handleRowUpdate = async (newData, oldData) => {
    if (window.event && window.event.keyCode === 13) {
      return oldData
    } else {
      const keyToIgnore = 'tableData'
      const cleanOldData = stripGridRowProps(oldData)
      const cleanNewData = {
        ...cleanOldData,
        ...stripGridRowProps(newData)
      }
      const objectEqual = compareData(cleanNewData, cleanOldData, keyToIgnore)
      if (objectEqual) {
        return oldData
      } else {
        const oldRowId = oldData[GRID_ROW_ID]
        const updatedData = fileList.map((row) => (
          row[GRID_ROW_ID] === oldRowId ? { ...cleanNewData, [GRID_ROW_ID]: oldRowId } : row
        ))
    
        Object.keys(cleanNewData).forEach(key => {
          const singleFileData = cleanNewData
          if (singleFileData[`${key}_verify`] !== undefined) {
            cleanNewData[`${key}_verify`] = singleFileData[key]
          }
        })
        const rowBoxId = cleanNewData.skpBoxId || cleanOldData.skpBoxId || props.boxId
        const rowFileId = cleanNewData.fileId || cleanOldData.fileId || cleanNewData.id || cleanOldData.id
        cleanNewData.skpBoxId = rowBoxId
        if (rowFileId) cleanNewData.fileId = rowFileId

        if (!rowBoxId || ((dataEntryType === 'New File' || dataEntryType === 'Maintenance File') && !rowFileId)) {
          errorToast({ message: t('toast.error') })
          return oldData
        }

        if (localEditOnly) {
          setFileList(updatedData)
          onRowsUpdated?.(updatedData)
          return { ...cleanNewData, [GRID_ROW_ID]: oldRowId }
        }

        const box = await gdeTransactions.getBox(jobId, rowBoxId)
        const boxStatus = box.data()?.status
        if (jobStatus !== constants.jobStatus.readyToTransmit && jobStatus !== constants.jobStatus.transmitted) {
          if (boxStatus !== constants.boxStatus.transmitted) {
            if (dataEntryType === 'New File' || dataEntryType === 'Maintenance File') {
              delete cleanNewData.id
              const fileInfoWithoutSrNo = { ...cleanNewData }
              delete fileInfoWithoutSrNo.Sr_No
              const fileData = {
                actionTs: new Date(),
                fileInfo: fileInfoWithoutSrNo,
              }
              await gdeTransactions.updateFile(jobId, rowBoxId, rowFileId, fileData)
            } else if (dataEntryType === 'New Box' || dataEntryType === 'Maintenance Box') {
              const boxInfoWithoutSrNo = { ...cleanNewData }
              delete boxInfoWithoutSrNo.Sr_No
              const boxData = {
                actionTs: new Date(),
                boxInfo: boxInfoWithoutSrNo,
              }
              await gdeTransactions.updateBoxData(jobId, rowBoxId, boxData)
            }
            setFileList(updatedData)
          }
        }

        return { ...cleanNewData, [GRID_ROW_ID]: oldRowId }
      }
    }
  }
  
  const ModalContent = (props) => {
    const { selectedFiles } = props
    const [enableSKPField, setEnableSKPFields] = useState(true)
    const [enableACC2Field, setEnableACC2Fields] = useState(true)
    const [enableACC3Field, setEnableACC3Fields] = useState(true)
    const [isUpdateDisabled, setIsUpdateDisabled] = useState(true)
    const [postObject, setPostObject] = useState({
      newBoxId: '',
      divId: '',
      deptId: '',
      targetBoxData: {}
    })
    const skpRef = useRef(null)
    const divRef = useRef(null)
    const deptRef = useRef(null)
    const skpBoxValue = async (e) => {
      if (e.keyCode === 13) {
        if (e.target.value !== '') {
          validateSkpBoxValue(e)
        }
      }
    }
    const onBlurskpBoxValue = async (e) => {
      if (e.target.value !== '') {
        validateSkpBoxValue(e)
      }
    }

    const validateSkpBoxValue = async (e) => {
      setBusy()
      const boxes = await gdeTransactions.getBox(props.jobId, e.target.value)
      // eslint-disable-next-line eqeqeq
      if (props.boxId === e.target.value) {
        skpRef?.current?.setErrorMsg(t('messages.sameErr'), true)
      } else if (!boxes.exists()) {
        skpRef?.current?.setErrorMsg(t('messages.notExist'), true)
        setIsUpdateDisabled(true)
      } else if (boxes.data().status === constants.boxStatus.transmitted || boxes.data().status === constants.boxStatus.exception || boxes.data().status === constants.boxStatus.requiresReKey) {
        if (boxes.data().status === constants.boxStatus.transmitted) {
          skpRef?.current?.setErrorMsg(t('messages.boxStatTransmit'), true)
        } else if (boxes.data().status === constants.boxStatus.exception) {
          skpRef?.current?.setErrorMsg(t('messages.boxStatException'), true)
        } else {
          skpRef?.current?.setErrorMsg(t('messages.boxStatRekeying'), true)
        }
        setIsUpdateDisabled(true)
      } else {
        setIsUpdateDisabled(false)
        setPostObject(prevState => ({
          ...prevState,
          newBoxId: e.target.value,
          targetBoxData: boxes.data()
        }))
      }

      clearBusy()
    }
    const onChangeCheckBox = (e) => {
      if (e.target.id === 'dataEntry_SKPBOX') {
        setEnableSKPFields(prevState => !prevState)
        skpRef.current.focus()
      } else if (e.target.id === 'dataEntry_DIV') {
        setEnableACC2Fields(prevState => !prevState)
      } else {
        setEnableACC3Fields(prevState => !prevState)
      }
    }


    const ButtonForYes = () => {
      const handleNo = () => {
        closeGeneralDialog()
        handleModal()
      }
      return (
        <>
          <BoxSetContainer>
            <FlexByRowCentered>
              <OutlinedButton id={'dataEntry_button_onNoMoreBoxToComplete'} onClick={() => handleNo()} noTranslate={true}>{props.t('buttons.no')}</OutlinedButton>
              <XDivider width='10px' />
              <ContainedButton id={'dataEntry_button_onHaveMoreBoxToComplete'} onClick={() => bulkPostService(selectedFiles, postObject, props.jobId, props.boxId)} noTranslate={true}>{props.t('buttons.yes')}</ContainedButton>
            </FlexByRowCentered>
          </BoxSetContainer>
        </>
      )
    }

    ButtonForYes.propTypes = {
      t: PropTypes.any
    }

    const onClickYes = () => {
      openGeneralDialog({
        // title: '',
        message: <DivWarning><Label>dataEntry.warning1</Label><pre> <CountStyle> {filesLength} </CountStyle></pre><Label>dataEntry.warning2</Label></DivWarning>,
        buttons: <ButtonForYes />,
        closeX: true
      })
    }

    const onCancel = () => {
      closeGeneralDialog()
    }

    const onChangeDiv = (e) => {
      if (e.keyCode === 13) {
        setPostObject(prevState => ({
          ...prevState,
          divId: e.target.value
        }))
      }
    }

    const onChangeDept = (e) => {
      if (e.keyCode === 13) {
        setPostObject(prevState => ({
          ...prevState,
          deptId: e.target.value
        }))
      }
    }

    return (
      <>
        <DivCenter>
          <Label noTranslate={true}>{t('dataEntry.selectFieldsMsg1')}</Label>
          <pre> <CountStyle> {filesLength} </CountStyle></pre>
          <Label noTranslate={true}>{t('dataEntry.selectFieldsMsg2')}</Label>
        </DivCenter>
        <DivWrap><Label noTranslate={true}>{t('dataEntry.selectFieldsMsg3')}</Label></DivWrap>
        <DivWrap><Label noTranslate={true}>{t('dataEntry.selectFieldsMsg4')}</Label></DivWrap>
        <BoxSetContainer>
          <YDivider height='30px' />
          <BoxSet>
            <Checkbox
              id={'dataEntry_SKPBOX'}
              onChange={(e) => onChangeCheckBox(e)}
            />
            <BoxTextfield>
              <TextField
                ref={skpRef}
                fieldTemplate={modalFormTemplate[SKPBOX]}
                value={props.boxId}
                disabled={enableSKPField}
                onKeyDown={(e) => skpBoxValue(e)}
                onBlur={(e) => onBlurskpBoxValue(e)}
                noTranslate={true}
              />
            </BoxTextfield>
          </BoxSet>
          <YDivider height='15px' />
          <BoxSet>
            <Checkbox
              id={'dataEntry_DIV'}
              onChange={(e) => onChangeCheckBox(e)}
            />
            <BoxTextfield>
              <TextField
                ref={divRef}
                fieldTemplate={modalFormTemplate[DIV]}
                value={props.accountLevel2}
                disabled={enableACC2Field}
                onKeyDown={(e) => onChangeDiv(e)}
                noTranslate={true}
              />
            </BoxTextfield>
          </BoxSet>
          <YDivider height='15px' />
          <BoxSet>
            <Checkbox
              id={'dataEntry_DEPT'}
              onChange={(e) => onChangeCheckBox(e)}
            />
            <BoxTextfield>
              <TextField
                ref={deptRef}
                fieldTemplate={modalFormTemplate[DEPT]}
                value={props.accountLevel3}
                disabled={enableACC3Field}
                onKeyDown={(e) => onChangeDept(e)}
                noTranslate={true}
              />
            </BoxTextfield>
          </BoxSet>
          <YDivider height='30px' />
        </BoxSetContainer>
        <BoxSetContainer>
          <FlexByRowCentered>
            <>
              <CancelButton onClick={onCancel} noTranslate={true} label={t('buttons.cancel')}/>
              <XDivider width='10px' />
              <ContainedButton disabled={isUpdateDisabled} onClick={() => onClickYes()} noTranslate={true}>{t('buttons.updateFields')}</ContainedButton>
            </>
          </FlexByRowCentered>
          <YDivider height='30px' />
        </BoxSetContainer>
      </>
    )
  }

  ModalContent.propTypes = {
    boxId: PropTypes.any,
    accountLevel2: PropTypes.any,
    accountLevel3: PropTypes.any,
    jobId: PropTypes.string,
    selectedFiles: PropTypes.array
  }


  const bulkPostService = async (selectedFiles, postObject, jobId, oldBoxId) => {
    const { newBoxId, divId, deptId, targetBoxData } = postObject
    // console.log('files', selectedFiles)
    const files = []
    let fileLength = 0

    // Checking current box
    const filesExisting = await gdeTransactions.getFilesForBox(jobId, newBoxId)
    const sourceBoxData = await gdeTransactions.getBox(jobId, oldBoxId)
    if (filesExisting.size > 0) {
      fileLength = parseInt(filesExisting.docs[filesExisting.size - 1].id)
    }

    // eslint-disable-next-line react/prop-types
    const fileListTemp = fileList.filter(item => !selectedFiles.includes(item))
    // eslint-disable-next-line
    selectedFiles.forEach((row, index) => {
      const fileNo = `${fileLength + 1}`
      const fileNumber = fileNo.padStart(5, '0')
      files[fileNumber] = row
      files[fileNumber].oldFileId = files[fileNumber].fileId
      files[fileNumber].fileId = fileNumber
      files[fileNumber].skpBoxId = newBoxId

      if (files[fileNumber].divisionId) {
        files[fileNumber].divisionId = divId !== '' ? divId : row?.divisionId
      }
      if (files[fileNumber].departmentId) {
        files[fileNumber].departmentId = deptId !== '' ? deptId : row?.departmentId
      }

      fileLength++
    })

    // newwwwwwwwwww
    // files.forEach(item => {
    //   console.log('item', item)
    //   return Object.keys(item).map(rowkeys => {
    //     const itemTemp = item[rowkeys]
    //     console.log('>>>>', itemTemp)
    //     delete itemTemp.tableData
    //     item.rowkeys = itemTemp
    //     return item
    //   })
    // }
    // )
    let fileCount = 0
    // updating status of box if the box already passed QC 
    if (targetBoxData?.status === constants.boxStatus.approved) {
      if (props.isFromQC) {
        await gdeTransactions.updateBoxData(jobId, newBoxId, { status: constants.boxStatus.readyForQc })
      } else {
        await gdeTransactions.updateBoxData(jobId, newBoxId, { status: props.statusTobeUpdated })
      }
    }
    // if ((sourceBoxData.data().status === constants.boxStatus.readyForQc || 
    // sourceBoxData.data().status === constants.boxStatus.qcInProcess) && fileListTemp.length === 0) {
    //   await gdeTransactions.updateBoxData(jobId, oldBoxId, { status: constants.boxStatus.exception })
    // }
    if (sourceBoxData.data().status === constants.boxStatus.requiresReKey ||
      sourceBoxData.data().status === constants.boxStatus.dataEntryInProcess) {
      fileCount = fileListTemp.length
    } else {
      fileCount = fileListTemp.length - 1
    }
    await gdeTransactions.moveFilesForBox(jobId, oldBoxId, newBoxId, files, targetBoxData)

    // change message
    successToast({ title: t('toast.bulkUpdateTitle') })
    closeGeneralDialog()
    // props.tableRef?.current?.setState(props.tableRef?.current?.dataManager?.getRenderState())
    // console.log('on close', props.tableRef.current.dataManager.data)
    props.onCloseTable(currentUserType, props.tableRef, jobId, oldBoxId, fileCount, true, fileListTemp)
  }

  const handleModal = (boxId, acc2, acc3, arrRefs, buttonRef, selectedFiles, jobId, modalRef) => {
    openGeneralDialog({
      title: <FlexByRowCentered><Label noTranslate={true}>{t('dataEntry.selectFields')}</Label></FlexByRowCentered>,
      message: <ModalContent arrRefs={arrRefs} boxId={boxId} accountLevel2={acc2} accountLevel3={acc3} jobId={jobId} modalRef={modalRef} selectedFiles={selectedFiles} t={t}/>,
      buttons: <></>,
      closeX: true
    })
  }

  const handleDeleteModal = (jobId, boxId, selectedFiles) => {
    if (dataEntryType === 'New File' || dataEntryType === 'Maintenance File') {
      if (filesLength === fileList.length && (currentUserType?.current !== userTypes.DATAENTRY_AND_VERIFY_USER && currentUserType?.current !== userTypes.DATAENTRY_USER)) {
        errorToast({ message: t('toast.restrictDeletionOfAllFiles') })
      } else {
        openGeneralDialog({
          title: <FlexByRowCentered><Label noTranslate='true'>{t('dataEntry.selectFilesToDelete')}</Label></FlexByRowCentered>,
          message: t('dataEntry.confirmRecordDelete'),
          buttons:
  <>
    <CancelButton onClick={onCancel} />
    <XDivider width='10px' />
    <ContainedButton onClick={() => handleDeleteFiles(jobId, boxId, selectedFiles)} >buttons.yes</ContainedButton>
  </>,
          closeX: true
        })
      }
    } else {
      if (filesLength === fileList.length && (currentUserType?.current !== userTypes.DATAENTRY_AND_VERIFY_USER && currentUserType?.current !== userTypes.DATAENTRY_USER)) {
        errorToast({ message: t('toast.restrictDeletionOfAllBoxes') })
      } else {
        openGeneralDialog({
          title: <FlexByRowCentered><Label>dataEntry.selectFilesToDelete</Label></FlexByRowCentered>,
          message: t('dataEntry.confirmRecordDelete'),
          buttons:
  <>
    <CancelButton onClick={onCancel} />
    <XDivider width='10px' />
    <ContainedButton onClick={() => handleDeleteBoxes(jobId, selectedFiles)} >buttons.yes</ContainedButton>
  </>,
          closeX: true
        })
      }
    }
  }

  const handleDeleteBoxes = async (jobId, selectedFiles) => {
    setBusy()
    selectedFiles.forEach(async (doc, index) => {
      await gdeTransactions.deleteBoxData(jobId, doc.skpBoxId || doc.boxId)
      const jobBoxMetricsData = await gdeTransactions.getMetricsForJobBox(jobId, doc.skpBoxId || doc.boxId)
      jobBoxMetricsData.forEach((doc) => {
        const data = doc.data()
        const { metricsFileId, jobCategory } = data
        if (metricsFileId !== undefined && jobCategory === 'Data Entry User') {
          const metricsData = {
            boxId: 0,
            totalBoxCount: 0,
            totalRecordCount: 0,
            updatedTs: new Date(),
          }
          gdeTransactions.updateMetricsForJobBox(doc.id, metricsData)
        }
      })
    })
    const boxCount = fileList.length - selectedFiles.length
    if (currentUserType?.current !== userTypes.DATAENTRY_AND_VERIFY_USER && currentUserType?.current !== userTypes.DATAENTRY_USER) {
      const data = {
        totalBoxes: boxCount,
      }
      await gdeTransactions.updateJob(jobId, data)
      history.push(`${routePaths.boxList}/${jobId}`)
    } else {
      setTimeout(props.onCancel(null, null, props.currentLockableFields, props.currentLockableFieldsData, dataEntryType), 50000)
    }

    setTimeout(successToast({ message: t('toast.boxDeleteSuccess', { deletedBoxesCount: selectedFiles.length }) }), 50000)
    closeGeneralDialog()
    clearBusy()
  }

  const handleDeleteFiles = async (jobId, boxId, selectedFiles) => {
    setBusy()
    const newFileList = []
    for (const [key, value] of Object.entries(selectedFiles)) {
      if (key) {
        const fileIndex = fileList.map(object => object.fileId).indexOf(value.fileId)
        await fileList.splice(fileIndex, 1)
        await gdeTransactions.deleteFile(jobId, boxId, value.fileId)
      }
    }

    const filesForBox2 = await gdeTransactions.getFilesForBox(jobId, boxId)

    fileList.forEach(async (doc) => {
      await gdeTransactions.deleteFile(jobId, boxId, doc.fileId)
    })

    const files2 = filesForBox2.docs.sort(function (a, b) {
      return a.id.localeCompare(b.id, undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    })

    const myPromise = new Promise(function (resolve, reject) {
      setTimeout(
        files2.forEach(async (doc, index) => {
          const docData = doc.data()
          const newCounter = index + 1
          const newFileName = newCounter.toString().padStart(5, '0')
          docData.fileInfo.fileId = newFileName
          docData.fileNumber = newFileName
          newFileList[index] = docData.fileInfo
          await gdeTransactions.createFile(jobId, boxId, newFileName, docData)
        })
        , 5000)
      resolve(newFileList)
    })

    const jobBoxMetricsData = await gdeTransactions.getMetricsForJobBox(jobId, boxId)
    jobBoxMetricsData.forEach((doc) => {
      const data = doc.data()
      const { metricsFileId, totalBoxCount, jobCategory } = data
      if (metricsFileId !== undefined && jobCategory === 'Data Entry User') {
        const metricsData = {
          totalRecordCount: newFileList.length,
          updatedTs: new Date(),
        }
        totalBoxCount > 0 && gdeTransactions.updateMetricsForJobBox(doc.id, metricsData)
      }
    })

    await gdeTransactions.updateBoxData(jobId, boxId, { totalFiles: files2.length })

    myPromise.then(function (newFileList) {
      setTimeout(successToast({ message: t('toast.filesDeleteSuccess', { deletedFilesCount: filesLength }) }), 50000)
      if (props.isFromQC) {
        // history.goBack()
        history.push(`${routePaths.boxQualityCheck}/${jobId}`)
        clearBusy()
      } else {
        if (newFileList.length === 0) {
          indexPointerOfFile = 0
        }
        setTimeout(props.onCloseTable(currentUserType, props.tableRef, jobId, boxId, indexPointerOfFile, true, newFileList, props.currentLockableFields, props.currentLockableFieldsData), 50000)
      }
      setTimeout(closeGeneralDialog(), 50000)
    })
  }

  const onCancel = () => {
    closeGeneralDialog()
  }

  const rows = useMemo(() => fileList.map((row, index) => {
    const gridRowId = getGridRowId(row, index)
    const { id: _legacyId, [GRID_ROW_ID]: _gridId, tableData: _tableData, ...rowData } = row || {}
    return {
      ...rowData,
      [GRID_ROW_ID]: gridRowId,
      tableData: { id: index },
    }
  }), [fileList])

  const duplicateValueCounts = useMemo(
    () => buildDuplicateValueCounts(fileList, columnsList),
    [fileList, columnsList],
  )

  // AI code start — upfront validation errors for inline cell display
  const formattedCellErrors = useMemo(() => {
    const formatted = {}
    Object.entries(cellValidationErrors || {}).forEach(([key, message]) => {
      formatted[key] = formatCellValidationMessage(message, t)
    })
    return formatted
  }, [cellValidationErrors, t])
  // AI code end

  useEffect(() => {
    if (!props.onValidationChange) return
    const mappedErrors = {}
    Object.entries(editErrors).forEach(([errorKey, message]) => {
      if (!message) return
      const row = rows.find(gridRow => errorKey.startsWith(`${gridRow[GRID_ROW_ID]}-`))
      if (!row) return
      const field = errorKey.slice(row[GRID_ROW_ID].length + 1)
      const rowIndex = row.tableData?.id ?? rows.indexOf(row)
      mappedErrors[`${rowIndex}${field}`] = message
    })
    const isRowEditing = Boolean(editingRowId)
    const syncKey = `${JSON.stringify(mappedErrors)}|${isRowEditing}`
    if (validationSyncKeyRef.current === syncKey) return
    validationSyncKeyRef.current = syncKey
    props.onValidationChange(mappedErrors, isRowEditing)
  }, [editErrors, editingRowId, rows, props.onValidationChange])

  const canEditRow = (row) => {
    if (!editTable) return false
    if (isFromQC) return true
    return !boxListWithStatus?.includes(row.skpBoxId) &&
      jobStatus !== constants.jobStatus.readyToTransmit &&
      jobStatus !== constants.jobStatus.transmitted
  }

  const activeRowHasErrors = editingRowId
    ? Object.keys(editErrors).some(key => key.startsWith(`${editingRowId}-`))
    : false

  const setValidationForField = (rowId, field, errorMessage) => {
    const errorKey = getEditErrorKey(rowId, field)
    setEditErrors(prevErrors => {
      if (prevErrors[errorKey] === errorMessage) return prevErrors
      if (!errorMessage && prevErrors[errorKey] === undefined) return prevErrors
      const nextErrors = { ...prevErrors }
      if (errorMessage) {
        nextErrors[errorKey] = errorMessage
      } else {
        delete nextErrors[errorKey]
      }
      return nextErrors
    })
  }

  const validateDraftField = (rowId, column, sourceField, nextDraft) => {
    if (!column.validate) {
      setValidationForField(rowId, sourceField, '')
      return ''
    }
    const validation = column.validate({ ...stripGridInternalProps(nextDraft) })
    const validationMessage = getValidationMessage(validation)
    setValidationForField(rowId, sourceField, validationMessage)
    return validationMessage
  }

  const handleDraftFieldChange = (rowId, column, sourceField, value) => {
    const nextDraft = {
      ...(editDraftRef.current || {}),
      [sourceField]: value
    }
    editDraftRef.current = nextDraft
    const result = validateDraftField(rowId, column, sourceField, nextDraft)
    const partnerField = DATE_PAIR_FIELDS[sourceField]
    if (partnerField) {
      const partnerColumn = columnsList.find((col) => col.field === partnerField)
      if (partnerColumn) {
        validateDraftField(rowId, partnerColumn, partnerField, nextDraft)
      }
    }
    return result
  }
  handleDraftFieldChangeRef.current = handleDraftFieldChange

  const handleEditClick = (id, row) => () => {
    captureGridScroll()
    setEditingRowId(id)
    const nextDraft = stripGridInternalProps(row)
    editDraftRef.current = nextDraft
    setEditDraft(nextDraft)

    const initialErrors = {}
    columnsList.forEach((column) => {
      const field = column.field
      if (!field || column.editable === 'never' || column.editable === false || !column.validate) {
        return
      }
      const validation = column.validate(nextDraft)
      const validationMessage = getValidationMessage(validation)
      if (validationMessage) {
        initialErrors[getEditErrorKey(id, field)] = validationMessage
      }
    })
    setEditErrors(initialErrors)
  }

  const handleSaveClick = (id, oldRow) => async () => {
    if (activeRowHasErrors) return
    captureGridScroll()
    const savedRow = await handleRowUpdate(editDraftRef.current || editDraft, oldRow)
    setEditingRowId(null)
    editDraftRef.current = null
    setEditDraft(null)
    setEditErrors({})
    return savedRow
  }

  const handleCancelClick = () => () => {
    captureGridScroll()
    setEditErrors({})
    setEditingRowId(null)
    editDraftRef.current = null
    setEditDraft(null)
  }

  const handleSelectionModelChange = (selectionModel) => {
    const selectionIds = new Set(selectionModel)
    onSelectionChange(rows.filter(row => selectionIds.has(row[GRID_ROW_ID])).map(stripGridRowProps))
  }

  const columns = useMemo(() => {
    // AI code — inline validation errors in view/edit cells
    const dataColumns = columnsList
      .filter(column => !column.hidden)
      .map((column, index) => {
        const field = column.field || `column-${index}`
        const gridField = columnsList.findIndex(c => (c.field || '') === field) === index ? field : `${field}-${index}`
        const editable = column.editable !== 'never' && column.editable !== false
        return {
          field: gridField,
          headerName: column.title,
          sortable: column.sortable !== false,
          editable: false,
          flex: 1,
          minWidth: column.minWidth || (field === 'Sr_No' ? 72 : 120),
          headerAlign: 'left',
          align: column.align || 'left',
          valueGetter: params => params.row[field],
          renderCell: (params) => {
            if (column.render) {
              return column.render(params.row)
            }
            const isEditing = params.id === editingRowId && editable
            if (!isEditing) {
              const value = params.row[field]
              const rowIndex = params.row.tableData?.id
              const cellErrorKey = String(rowIndex) + field
              const formattedError = formattedCellErrors[cellErrorKey]
              const duplicateKey = `${field}\u0000${String(value ?? '')}`
              const hasDuplicateValue = Boolean(column.doNotDuplicate && value) &&
                (duplicateValueCounts.get(duplicateKey) || 0) > 1
              const helperText = formattedError || (hasDuplicateValue ? t('dataEntry.duplicateValue') : '')
              return (
                <QcViewCell
                  helperText={helperText}
                  value={value}
                />
              )
            }
            const value = editDraftRef.current?.[field] ?? editDraft?.[field] ?? ''
            const editErrorKey = getEditErrorKey(params.id, field)
            const helperText = formatCellValidationMessage(editErrors[editErrorKey], t) || ''
            return (
              <QcManualEditTextField
                autoFocus={false}
                column={column}
                field={field}
                helperText={helperText}
                onDraftChange={(...args) => handleDraftFieldChangeRef.current(...args)}
                rowId={params.id}
                value={value}
              />
            )
          },
        }
      })

    if (!editTable) {
      return dataColumns
    }

    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 88,
        sortable: false,
        headerAlign: 'left',
        align: 'left',
        getActions: ({ id, row }) => {
          if (!canEditRow(stripGridRowProps(row))) return []
          const isInEditMode = id === editingRowId
          if (isInEditMode) {
            return [
              <GridActionsCellItem key="save" disabled={activeRowHasErrors} icon={<DoneOutlined />} label="Save" onClick={handleSaveClick(id, row)} />,
              <GridActionsCellItem key="cancel" icon={<ClearOutlined />} label="Cancel" onClick={handleCancelClick()} />,
            ]
          }
          return [
            <GridActionsCellItem key="edit" disabled={Boolean(editingRowId)} icon={<EditIcon />} label="Edit" onClick={handleEditClick(id, row)} />,
          ]
        },
      },
      ...dataColumns
    ]
  }, [
    columnsList,
    editTable,
    editingRowId,
    editErrors,
    editDraft,
    formattedCellErrors,
    duplicateValueCounts,
    boxListWithStatus,
    jobStatus,
    isFromQC,
    activeRowHasErrors,
    t,
  ])

  const formatValidationMessage = useCallback(
    (message) => formatCellValidationMessage(message, t),
    [t],
  )

  const errorLinesByRowIndex = useMemo(
    () => buildErrorLinesByRowIndex(formattedCellErrors),
    [formattedCellErrors],
  )

  // AI code start — dynamic row height for validation messages
  const resolveRowHeight = useCallback((row) => {
    if (!useCompactQcLayout) {
      return 78
    }
    const rowId = row[GRID_ROW_ID]
    const rowIndex = row.tableData?.id
    const isEditing = rowId === editingRowId
    let maxLines = errorLinesByRowIndex.get(rowIndex) || 0
    if (isEditing) {
      maxLines = Math.max(maxLines, getEditErrorMaxLines(rowId, editErrors, formatValidationMessage))
    }
    const baseHeight = isEditing ? QC_GRID_EDIT_ROW_BASE_HEIGHT : QC_GRID_VIEW_ROW_HEIGHT
    return estimateRowHeightFromLines(baseHeight, maxLines)
  }, [
    useCompactQcLayout,
    editingRowId,
    editErrors,
    errorLinesByRowIndex,
    formatValidationMessage,
  ])

  const getRowHeight = useCallback((params) => {
    if (!useCompactQcLayout) {
      return 78
    }
    if (rowNeedsDynamicHeight(params.model, editingRowId, errorLinesByRowIndex)) {
      return 'auto'
    }
    return QC_GRID_VIEW_ROW_HEIGHT
  }, [useCompactQcLayout, editingRowId, errorLinesByRowIndex])

  const getEstimatedRowHeight = useCallback((params) => (
    resolveRowHeight(params.model)
  ), [resolveRowHeight])

  const gridContainerHeight = useMemo(() => {
    if (!useCompactQcLayout) {
      return QC_GRID_DEFAULT_HEIGHT
    }
    return computeQcGridContainerHeight(resolveRowHeight, rows)
  }, [rows, resolveRowHeight, useCompactQcLayout])

  useEffect(() => {
    if (!useCompactQcLayout) {
      return undefined
    }
    const frameId = requestAnimationFrame(() => {
      apiRef.current?.resetRowHeights?.()
    })
    return () => cancelAnimationFrame(frameId)
  }, [useCompactQcLayout, cellValidationErrors, editErrors, editingRowId])
  // AI code end

  const enableSelection = !isViewAllFiles && (isBoxList !== 'boxList' && isBoxList !== 'viewAllfileQualityCheck')

  const onAddNewFile = async (boxId, jobId) => {
    await gdeTransactions.updateBoxStatus(jobId, boxId, constants.boxStatus.dataEntryInProcess, false, null, fileList.length)
    history.push(`${routePaths.dataEntry}/${jobId}/${boxId}/AddNewFilesByQC`)
  }

  const gridSx = {
    boxSizing: 'border-box',
    minWidth: 0,
    width: '100%',
    borderColor: '#e0e0e0',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.24)',
    color: 'rgba(0, 0, 0, 0.87)',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: '0.875rem',
    '& .MuiDataGrid-toolbarContainer': {
      alignItems: 'flex-start',
      borderBottom: 'none',
      justifyContent: 'flex-end',
      minHeight: useCompactQcLayout ? `${QC_GRID_TOOLBAR_HEIGHT}px` : '48px',
      padding: useCompactQcLayout ? '4px 18px 0 16px' : '8px 18px 0 16px',
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
      color: '#000',
      fontSize: 0,
      height: '32px',
      minWidth: '32px',
      padding: 0
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-startIcon': {
      margin: 0
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-endIcon': {
      display: 'none'
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-startIcon svg': {
      fontSize: '26px'
    },
    '& .MuiDataGrid-toolbarContainer .MuiButton-root:hover': {
      backgroundColor: 'transparent'
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#fff',
      borderBottom: '1px solid #e0e0e0',
      color: 'rgba(0, 0, 0, 0.87)',
      fontWeight: 700,
      minHeight: useCompactQcLayout ? `${QC_GRID_HEADER_HEIGHT}px` : '68px',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
      whiteSpace: 'normal'
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      justifyContent: 'flex-start'
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: '8px',
      paddingRight: '8px'
    },
    '& .MuiDataGrid-cell': {
      alignItems: 'flex-start',
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      lineHeight: '1.35',
      outline: 'none',
      overflow: useCompactQcLayout ? 'visible' : 'hidden',
      paddingBottom: useCompactQcLayout ? '6px' : 0,
      paddingTop: useCompactQcLayout ? '6px' : 0,
      paddingLeft: '8px',
      paddingRight: '8px'
    },
    // AI code — dynamic row height cell overflow (QC layout)
    '& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell, & .MuiDataGrid-row--dynamicHeight .MuiDataGrid-cellContent': {
      overflow: 'visible',
      whiteSpace: 'normal',
    },
    '& .MuiDataGrid-row--dynamicHeight': {
      maxHeight: 'none !important',
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none'
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },
    '& .qc-row-disabled': {
      color: 'rgba(0, 0, 0, 0.22)',
      pointerEvents: 'none'
    },
    '& .qc-row-disabled .MuiDataGrid-cell, & .qc-row-disabled .MuiSvgIcon-root, & .qc-row-disabled .MuiIconButton-root': {
      color: 'rgba(0, 0, 0, 0.22)'
    },
    '& .qc-row-editing': {
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.28)',
      zIndex: 1,
      maxHeight: 'none !important',
    },
    '& .qc-row-editing .MuiDataGrid-cell': {
      overflow: 'visible',
      alignItems: 'flex-start',
    },
    '& .qc-row-editing .MuiDataGrid-cell:not(.MuiDataGrid-actionsCell)': {
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingTop: '8px',
      paddingBottom: '8px',
    },
    '& .qc-row-editing .MuiTextField-root': {
      width: '100%'
    },
    '& .qc-row-editing .MuiInputBase-root': {
      color: 'rgba(0, 0, 0, 0.87)',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontSize: '0.875rem',
      lineHeight: '1.43'
    },
    '& .qc-row-editing .MuiFormHelperText-root': {
      color: '#f44336',
      fontSize: '0.75rem',
      lineHeight: 1.35,
      marginLeft: 0,
      marginTop: '4px',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'visible',
    },
    '& .MuiDataGrid-cell--editing': {
      backgroundColor: 'transparent',
      boxShadow: 'none'
    },
    '& .MuiDataGrid-cell--editing:focus-within': {
      outline: 'none'
    },
    '& .MuiDataGrid-cell--editing .MuiTextField-root': {
      justifyContent: 'center'
    },
    '& .MuiDataGrid-cell--editing .MuiInputBase-root': {
      color: 'rgba(0, 0, 0, 0.87)',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontSize: '0.875rem',
      lineHeight: '1.43'
    },
    '& .MuiDataGrid-cell--editing .MuiFormHelperText-root': {
      color: '#f44336',
      fontSize: '0.75rem',
      lineHeight: 1.35,
      marginLeft: 0,
      marginTop: '4px'
    },
    '& .MuiDataGrid-actionsCell': {
      gap: '2px'
    },
    '& .MuiDataGrid-actionsCell .MuiIconButton-root': {
      color: '#000',
      padding: '8px'
    },
    '& .MuiDataGrid-actionsCell .MuiIconButton-root.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26)'
    },
    '& .MuiDataGrid-actionsCell .MuiSvgIcon-root': {
      fontSize: '28px'
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: 'none',
      minHeight: useCompactQcLayout ? `${QC_GRID_FOOTER_HEIGHT}px` : '72px',
      paddingRight: '12px'
    },
    '& .MuiTablePagination-root': {
      color: 'rgba(0, 0, 0, 0.87)',
      fontSize: '0.875rem'
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      fontSize: '0.875rem'
    },
    '& .MuiTablePagination-selectLabel': {
      display: 'none'
    },
    '& .MuiTablePagination-select': {
      paddingRight: '24px'
    },
  }

  let actionToolbar = null
  if (!qualityCheck) {
    // AI code start — file QC toolbar (no float overlap)
    if (isFromQC) {
      actionToolbar = (
        <QcFileActionToolbar>
          {isBoxList === 'boxList'
            ? null
            : (currentUserType?.current === userTypes.VERIFICATION_USER || currentUserType?.current === userTypes.DATAENTRY_AND_VERIFY_USER || currentUserType?.current === userTypes.DATAENTRY_USER || currentUserType?.current === undefined) && (
              <ThemeButton
                id="dataEntry_button_select_files_to_delete"
                variant="contained"
                color="primary"
                onClick={() => { handleDeleteModal(props.jobId, props.boxId, selectedFiles) }}
                disabled={enable}
                noTranslate={true}
              >
                {t('buttons.selectFilesToDelete')}
              </ThemeButton>
              )}
          {isBoxList === 'boxList' || (isBoxList === 'dataEntry' && (currentTemplateType === 'New Box' || currentTemplateType === 'Maintenance Box'))
            ? null
            : (
              <ThemeButton
                id="dataEntry_button_select_fieldstoupdate"
                variant="contained"
                color="primary"
                onClick={() => { handleModal(props.boxId, props.acc2, props.acc3, props.arrRefs, props.buttonRef, selectedFiles, props.jobId) }}
                disabled={enable}
                noTranslate={true}
              >
                {t('buttons.selectFieldsToUpdate')}
              </ThemeButton>
              )}
          <ThemeButton
            id="dataEntry_button_addNewFile"
            variant="contained"
            color="primary"
            onClick={() => { onAddNewFile(props.boxId, props.jobId) }}
            noTranslate={true}
          >
            {t('dataEntry.addEditFile')}
          </ThemeButton>
        </QcFileActionToolbar>
      )
    // AI code end
    } else {
      actionToolbar = (
        <BoxFlex>
          <>
            {isBoxList === 'boxList'
              ? null
              : (currentUserType?.current === userTypes.VERIFICATION_USER || currentUserType?.current === userTypes.DATAENTRY_AND_VERIFY_USER || currentUserType?.current === userTypes.DATAENTRY_USER || currentUserType?.current === undefined) && <BottomPaddedButton id={'dataEntry_button_select_fieldstoupdate'} variant='contained' color='primary' onClick={() => { handleDeleteModal(props.jobId, props.boxId, selectedFiles) }} noTranslate={true} disabled={enable}>
                {t('buttons.selectFilesToDelete')}
              </BottomPaddedButton>}
            <XDivider width="20px" />
            {isBoxList === 'boxList' || (isBoxList === 'dataEntry' && (currentTemplateType === 'New Box' || currentTemplateType === 'Maintenance Box'))
              ? null
              : <BottomPaddedButton id={'dataEntry_button_select_fieldstoupdate'} variant='contained' color='primary' onClick={() => { handleModal(props.boxId, props.acc2, props.acc3, props.arrRefs, props.buttonRef, selectedFiles, props.jobId) }} disabled={enable} noTranslate={true}>
                {t('buttons.selectFieldsToUpdate')}
              </BottomPaddedButton>}
            {props.isFromQC &&
              <>
                <XDivider width="20px" />
                <BottomPaddedButton id={'dataEntry_button_addNewFile'} variant="contained" color="primary" onClick={() => { onAddNewFile(props.boxId, props.jobId) }} noTranslate={true}>
                  {t('dataEntry.addEditFile')}
                </BottomPaddedButton>
                <XDivider width="20px" />
              </>}
          </>
        </BoxFlex>
      )
    }
  }

  return (
    <>
      <LocalToast ref={refLocalToast} />
      {actionToolbar}
      {!useCompactQcLayout ? <YDivider height='30px' /> : null}
      <TableContainer style={useCompactQcLayout ? { paddingBottom: 0 } : undefined}>
        <GridContainer style={{ height: gridContainerHeight }}>
          <DataGrid
            apiRef={apiRef}
            loading={Boolean(filesLoading)}
            rows={rows}
            columns={columns}
            getRowId={(row) => row[GRID_ROW_ID]}
            getRowClassName={(params) => {
              if (!editingRowId) return ''
              return params.id === editingRowId ? 'qc-row-editing' : 'qc-row-disabled'
            }}
            onRowSelectionModelChange={handleSelectionModelChange}
            checkboxSelection={enableSelection}
            disableRowSelectionOnClick
            disableColumnMenu
            getRowHeight={useCompactQcLayout ? getRowHeight : undefined} // AI code — QC dynamic row height
            getEstimatedRowHeight={useCompactQcLayout ? getEstimatedRowHeight : undefined}
            rowHeight={useCompactQcLayout ? undefined : 78}
            virtualizeColumnsWithAutoRowHeight={useCompactQcLayout}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={QC_GRID_PAGE_SIZE_OPTIONS}
            rowBuffer={QC_GRID_ROW_BUFFER}
            columnHeaderHeight={useCompactQcLayout ? QC_GRID_HEADER_HEIGHT : 68}
            slots={{
              toolbar: QcGridToolbar,
            }}
            slotProps={{
              toolbar: {
                setColumnsButtonEl,
              },
              panel: {
                anchorEl: columnsButtonEl,
                placement: 'bottom-end',
                sx: {
                  '& .MuiDataGrid-panelContent': {
                    maxHeight: 'min(400px, 60vh)',
                    overflowY: 'auto',
                  },
                },
              },
            }}
            sx={{ ...gridSx, height: '100%' }}
          />
        </GridContainer>
      </TableContainer>
    </>
  )
}

QcDataGridBulkUpdateTable.propTypes = {
  tableRef: PropTypes.any,
  indexPointerOfFile: PropTypes.any,
  fileList: PropTypes.array,
  columnsList: PropTypes.array,
  boxId: PropTypes.string,
  acc2: PropTypes.string,
  acc3: PropTypes.array,
  arrRefs: PropTypes.array,
  buttonRef: PropTypes.array,
  jobId: PropTypes.string,
  statusTobeUpdated: PropTypes.any,
  currentLockableFields: PropTypes.any,
  currentLockableFieldsData: PropTypes.any,
  onCloseTable: PropTypes.func,
  onCancel: PropTypes.func,
  isFromQC: PropTypes.bool,
  isViewAllFiles: PropTypes.string,
  currentUserType: PropTypes.string,
  qualityCheck: PropTypes.bool,
  currentTemplateType: PropTypes.any,
  editTable: PropTypes.bool,
  boxListWithStatus: PropTypes.array,
  jobStatus: PropTypes.string,
  onValidationChange: PropTypes.func,
  onSelectionChange: PropTypes.func,
  localEditOnly: PropTypes.bool,
  filesLoading: PropTypes.bool,
  // AI code — QC inline validation props
  cellValidationErrors: PropTypes.object,
  onRowsUpdated: PropTypes.func,
}

export default QcDataGridBulkUpdateTable
